import { useState, useEffect } from 'react';
import { notificationApi } from '../api/notificationApi';
import { Bell, Check, CheckCheck, Trash2, Calendar, Phone, FileText, AlertTriangle, Clock, CheckCircle, XCircle, Inbox } from '../icons/wp-icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/apiClient';
import { kvaBestaetingApi } from '../api/systemSettingsApi';
import { useNotifications } from '../context/NotificationContext';
import Layout from '../components/Layout/Layout';
import toast from 'react-hot-toast';
import CounterProposalModal from '../components/Notifications/CounterProposalModal';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  // IDs von Notifications, für die aktuell ein Action-Request läuft (Doppelklick-Schutz).
  const [processingIds, setProcessingIds] = useState(() => new Set());
  const navigate = useNavigate();
  const { decrementUnread, clearUnread, refreshUnreadCount } = useNotifications();

  const isProcessing = (id) => processingIds.has(id);
  const withProcessingLock = async (id, fn) => {
    if (processingIds.has(id)) return; // Doppelklick verhindern
    setProcessingIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      return await fn();
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, []);

  // Client-seitig gefilterte Benachrichtigungen (kein API-Call beim Filterwechsel)
  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  // Polling für automatische Aktualisierung alle 10 Sekunden
  useEffect(() => {
    const pollInterval = setInterval(() => {
      loadNotifications(true); // silent reload
    }, 10000);

    return () => clearInterval(pollInterval);
  }, []);

  const loadNotifications = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      // Immer alle Benachrichtigungen laden - Filterung erfolgt client-seitig
      const data = await notificationApi.getAll(false);
      setNotifications(data.notifications);
      // Badge aktualisieren bei silent reload
      if (silent && data.unreadCount !== undefined) {
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Telefonnummer aus Nachricht extrahieren
  const extractPhoneNumber = (message) => {
    // Suche nach Tel: oder Telefon: gefolgt von einer Nummer
    const telMatch = message.match(/Tel(?:efon)?:\s*([\d\s\-\/]+)/i);
    if (telMatch) {
      return telMatch[1].replace(/[\s\-\/]/g, '');
    }
    // Suche nach Telefonnummer-Mustern
    const phoneMatch = message.match(/(\+?[\d\s\-\/]{8,})/);
    if (phoneMatch) {
      return phoneMatch[1].replace(/[\s\-\/]/g, '');
    }
    return null;
  };

  const handleMarkAsRead = async (id, e) => {
    e?.stopPropagation();
    try {
      const wasUnread = notifications.find(n => n.id === id)?.read === false;
      await notificationApi.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      // Sofort Badge aktualisieren
      if (wasUnread) {
        decrementUnread();
      }
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      // Sofort Badge auf 0 setzen
      clearUnread();
    } catch (error) {
      console.error('Mark all as read error:', error);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try {
      const wasUnread = notifications.find(n => n.id === id)?.read === false;
      await notificationApi.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Badge aktualisieren wenn ungelesen gelöscht wurde
      if (wasUnread) {
        decrementUnread();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm('Möchten Sie wirklich alle Benachrichtigungen löschen?')) {
      return;
    }
    try {
      await notificationApi.deleteAll();
      setNotifications([]);
      refreshUnreadCount();
    } catch (error) {
      console.error('Delete all error:', error);
    }
  };

  const handleCall = (phoneNumber, e) => {
    e?.stopPropagation();
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Bei Rückruf-Anfragen: Direkt anrufen wenn Telefonnummer vorhanden
    if (notification.type === 'RUECKRUF_ANFRAGE') {
      const phoneNumber = notification.data?.telefon || extractPhoneNumber(notification.message);
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
        return;
      }
    }

    // Bei täglichen Erinnerungen mit klickbaren Items: Nicht automatisch navigieren
    if (notification.type === 'TAEGLICHE_ERINNERUNG' && (notification.data?.anfragen || notification.data?.termine)) {
      return; // Items sind einzeln klickbar
    }

    if (notification.linkTo) {
      navigate(notification.linkTo);
    }
  };

  const handleAction = async (notification, action, e) => {
    e?.stopPropagation();
    try {
      // Fallback: Actions ohne endpoint routen auf den Standard-Confirm/Reject-Flow,
      // damit AENDERUNG_TELEFON & Co. nicht am fehlenden action.endpoint crashen.
      if (!action?.endpoint) {
        if (action?.action === 'confirm') return await handleConfirmAppointment(notification, e);
        if (action?.action === 'reject')  return await handleRejectAppointment(notification, e);
        console.warn('Action ohne endpoint und ohne confirm/reject-Fallback:', action);
        return;
      }
      if (action.endpoint.startsWith('/api/')) {
        await apiClient.post(action.endpoint);
      } else if (action.endpoint.startsWith('tel:')) {
        window.location.href = action.endpoint;
      } else {
        navigate(action.endpoint);
      }

      if (!notification.read) {
        handleMarkAsRead(notification.id);
      }
    } catch (error) {
      console.error('Action error:', error);
      alert('Fehler beim Ausführen der Aktion');
    }
  };

  // Terminanfrage bestätigen (Email-Agent Integration)
  const handleConfirmAppointment = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        await apiClient.post(`/notifications/${notification.id}/confirm`);

        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '✅ ' + n.title.replace(/^📅 |^🔄 |^🚫 /, ''), message: n.message + '\n\n✅ Bestätigt' }
            : n
          )
        );
        decrementUnread();

        const data = notification.data || {};
        const isTelefon = ['telefonassistent', 'elevenlabs'].includes((data.quelle || '').toLowerCase()) || data.typ?.includes('TELEFON');
        const isWhatsApp = data.quelle === 'whatsapp';
        toast.success(isTelefon
          ? 'Termin wurde bestätigt! Der Kunde wird telefonisch benachrichtigt.'
          : isWhatsApp
          ? 'Termin wurde bestätigt! Der Kunde wird per WhatsApp benachrichtigt.'
          : 'Termin wurde bestätigt! Der Kunde erhält eine Bestätigungs-E-Mail.');
      } catch (error) {
        console.error('Confirm appointment error:', error);
        toast.error('Fehler beim Bestätigen: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // Terminanfrage ablehnen (Email-Agent Integration)
  const handleRejectAppointment = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        await apiClient.post(`/notifications/${notification.id}/reject`);

        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '❌ ' + n.title.replace(/^📅 |^🔄 |^🚫 /, ''), message: n.message + '\n\n❌ Abgelehnt' }
            : n
          )
        );
        decrementUnread();

        const data = notification.data || {};
        const isTelefon = ['telefonassistent', 'elevenlabs'].includes((data.quelle || '').toLowerCase()) || data.typ?.includes('TELEFON');
        const isWhatsApp = data.quelle === 'whatsapp';
        toast.success(isTelefon
          ? 'Termin wurde abgelehnt. Der Kunde wird telefonisch benachrichtigt.'
          : isWhatsApp
          ? 'Termin wurde abgelehnt. Der Kunde wird per WhatsApp informiert.'
          : 'Termin wurde abgelehnt. Der Kunde wurde informiert.');
      } catch (error) {
        console.error('Reject appointment error:', error);
        toast.error('Fehler beim Ablehnen: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // Gegenvorschlag
  const [counterProposalNotification, setCounterProposalNotification] = useState(null);
  const [counterProposalLoading, setCounterProposalLoading] = useState(false);

  const handleCounterPropose = (notification, e) => {
    e?.stopPropagation();
    setCounterProposalNotification(notification);
  };

  const handleSubmitCounterProposal = async ({ datum, uhrzeit }) => {
    if (!counterProposalNotification) return;
    setCounterProposalLoading(true);
    try {
      await apiClient.post(`/notifications/${counterProposalNotification.id}/counter-propose`, { datum, uhrzeit });
      setNotifications(prev =>
        prev.map(n => n.id === counterProposalNotification.id
          ? { ...n, read: true, title: '🔄 ' + n.title.replace(/^📅 |^🔄 |^🚫 /, '') }
          : n
        )
      );
      decrementUnread();
      const data = counterProposalNotification.data || {};
      const isWhatsApp = data.quelle === 'whatsapp';
      const isPhone = ['telefonassistent', 'elevenlabs'].includes((data.quelle || '').toLowerCase());
      toast.success(isPhone
        ? `Gegenvorschlag (${datum} um ${uhrzeit} Uhr) wird per Rückanruf übermittelt.`
        : isWhatsApp
        ? `Gegenvorschlag (${datum} um ${uhrzeit} Uhr) per WhatsApp gesendet.`
        : `Gegenvorschlag (${datum} um ${uhrzeit} Uhr) per E-Mail gesendet.`);
      setCounterProposalNotification(null);
    } catch (error) {
      console.error('Counter-propose error:', error);
      toast.error('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setCounterProposalLoading(false);
    }
  };

  // KVA bestätigen/genehmigen und per E-Mail senden
  const handleConfirmKva = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        const kvaId = notification.data?.kvaId;
        const kvaBestaetungId = notification.data?.kvaBestaetungId;
        const quelle = notification.data?.quelle;
        const isAb = notification.type === 'AB_ERSTELLT';

        if (isAb && kvaId) {
          const channel = quelle === 'whatsapp' ? 'whatsapp' : 'email';
          await apiClient.post(`/kostenvoranschlaege/${kvaId}/send-ab`, { channel });
          await notificationApi.markAsRead(notification.id);
        } else if (kvaId) {
          const channel = quelle === 'whatsapp' ? 'whatsapp' : 'email';
          await apiClient.post(`/kostenvoranschlaege/${kvaId}/send`, { channel });
          await notificationApi.markAsRead(notification.id);
        } else if (kvaBestaetungId) {
          await kvaBestaetingApi.approve(kvaBestaetungId);
        } else {
          await apiClient.post(`/notifications/${notification.id}/confirm`);
        }

        const dokumentTyp = isAb ? 'AB' : 'KVA';
        const kanalText = quelle === 'whatsapp' ? 'per WhatsApp' : 'per E-Mail';
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '✅ ' + n.title.replace(/^📄 /, '').replace(/^📋 /, ''), message: n.message + `\n\n✅ ${dokumentTyp} versendet` }
            : n
          )
        );
        decrementUnread();
        toast.success(`${dokumentTyp} wurde ${kanalText} an den Kunden gesendet!`);
      } catch (error) {
        console.error('Confirm KVA error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // KVA ablehnen
  const handleRejectKva = async (notification, e) => {
    e?.stopPropagation();
    const grund = prompt('Grund für Ablehnung (optional):');
    await withProcessingLock(notification.id, async () => {
      try {
        const kvaBestaetungId = notification.data?.kvaBestaetungId;
        if (kvaBestaetungId) {
          await kvaBestaetingApi.reject(kvaBestaetungId, grund);
        } else {
          await apiClient.post(`/notifications/${notification.id}/reject`, { grund });
        }
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '❌ ' + n.title.replace(/^💰 /, ''), message: n.message + '\n\n❌ KVA abgelehnt' }
            : n
          )
        );
        decrementUnread();
        toast.success('KVA wurde abgelehnt.');
      } catch (error) {
        console.error('Reject KVA error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // KVA bearbeiten - Als Entwurf erstellen und zur Bearbeitungsseite navigieren
  const handleEditKva = async (notification, e) => {
    e?.stopPropagation();
    const kvaId = notification.data?.kvaId;
    const kvaBestaetungId = notification.data?.kvaBestaetungId;

    if (kvaId) {
      navigate(`/kostenvoranschlaege/${kvaId}/bearbeiten`);
    } else if (kvaBestaetungId) {
      try {
        const response = await apiClient.post(`/kva-bestaetigung/${kvaBestaetungId}/approve-as-draft`);
        const newKvaId = response.data.kva?.id;
        if (newKvaId) {
          setNotifications(prev =>
            prev.map(n => n.id === notification.id
              ? { ...n, read: true, data: { ...n.data, kvaId: newKvaId } }
              : n
            )
          );
          toast.success('KVA als Entwurf erstellt — Sie können ihn jetzt bearbeiten.');
          navigate(`/kostenvoranschlaege/${newKvaId}/bearbeiten`);
        } else {
          navigate('/kostenvoranschlaege');
        }
      } catch (error) {
        console.error('KVA als Entwurf erstellen fehlgeschlagen:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    } else {
      navigate('/kostenvoranschlaege');
    }
  };

  // Prüfen ob Notification eine Aktion erfordert (Bestätigen/Ablehnen)
  // Buttons werden auch nach Lesen angezeigt, bis Entscheidung getroffen wurde
  const isAppointmentSuggestion = (notification) => {
    // 🔄-Prefix-Check ENTFERNT: AENDERUNG_TELEFON-Notifications nutzen 🔄 inhaltlich als
    // Icon ("Änderung"), nicht als Status-Marker. data.aktion ist die Single-Source-of-Truth
    // und wird nach Bearbeitung auf 'erledigt' gesetzt.
    return (notification.type === 'TERMIN_BESTAETIGUNG_ANFRAGE' ||
            notification.type === 'TERMIN_AENDERUNG_ANFRAGE' ||
            notification.type === 'TERMIN_STORNIERUNG_ANFRAGE') &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Prüfen ob Notification eine KVA-Bestätigung ist (Buttons auch nach Lesen anzeigen)
  const isKvaBestaetigung = (notification) => {
    return notification.type === 'KVA_BESTAETIGUNG_ANFRAGE' &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Prüfen ob Notification eine AB-Bestätigung ist
  const isAbBestaetigung = (notification) => {
    return notification.type === 'AB_ERSTELLT' &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Button-Labels basierend auf Typ
  const getActionLabels = (notification) => {
    const typ = notification.data?.typ;
    if (typ === 'STORNIERUNG') {
      return { confirm: 'Stornierung bestätigen', reject: 'Ablehnen' };
    }
    if (typ === 'TERMIN_AENDERUNG') {
      return { confirm: 'Änderung bestätigen', reject: 'Ablehnen' };
    }
    return { confirm: 'Termin bestätigen', reject: 'Ablehnen' };
  };

  // Extrahiere wichtige Termin-Infos aus Notification für Desktop-Vorschau
  const getTerminInfos = (notification) => {
    const data = notification.data || {};
    const infos = [];

    // Kunde
    if (data.kundeName) {
      infos.push({ label: 'Kunde', value: data.kundeName });
    }

    // Termin-Daten
    if (data.terminDaten) {
      infos.push({ label: 'Termin', value: `${data.terminDaten.datum} um ${data.terminDaten.uhrzeit} Uhr` });
    }

    // Bei Terminänderung: Alter und neuer Termin
    if (data.typ === 'TERMIN_AENDERUNG') {
      if (data.alterTermin) {
        infos.push({ label: 'Alter Termin', value: `${data.alterTermin.datum} um ${data.alterTermin.uhrzeit} Uhr`, type: 'old' });
      }
      if (data.neuerTermin) {
        infos.push({ label: 'Neuer Termin', value: `${data.neuerTermin.datum} um ${data.neuerTermin.uhrzeit} Uhr`, type: 'new' });
      }
    }

    // Adresse
    if (data.kundeAdresse) {
      infos.push({ label: 'Adresse', value: data.kundeAdresse });
    }

    return infos;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'text-red-600 bg-red-50 border-red-200';
      case 'HIGH': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'NORMAL': return 'text-primary bg-primary/10 border-primary/20';
      case 'LOW': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  const getTypeIcon = (notification) => {
    const type = typeof notification === 'string' ? notification : notification?.type;
    const quelle = typeof notification === 'object' ? notification?.data?.quelle : null;

    // WhatsApp-Quelle: Immer WhatsApp-Icon zeigen
    if (quelle === 'whatsapp') {
      return <WhatsAppIcon className="w-6 h-6 text-green-600" />;
    }

    switch (type) {
      case 'TERMIN_BESTAETIGUNG_ANFRAGE':
      case 'TERMIN_AENDERUNG_ANFRAGE':
      case 'TERMIN_STORNIERUNG':
      case 'TERMIN_STORNIERUNG_ANFRAGE':
      case 'TERMIN_BESTAETIGT':
      case 'TERMIN_STORNIERT':
      case 'TERMIN_KONFLIKT':
      case 'ERINNERUNG_TERMIN':
        return <Calendar className="w-6 h-6" />;
      case 'BESCHWERDE':
        return <AlertTriangle className="w-6 h-6" />;
      case 'MANUELL_PRUEFEN':
        return <AlertTriangle className="w-6 h-6" />;
      case 'RUECKRUF_ANFRAGE':
        return <Phone className="w-6 h-6" />;
      case 'RECHNUNG_ERSTELLT':
        return <FileText className="w-6 h-6" />;
      case 'ZAHLUNG_UEBERFAELLIG':
        return <AlertTriangle className="w-6 h-6" />;
      case 'TAEGLICHE_ERINNERUNG':
        return <Inbox className="w-6 h-6" />;
      default:
        return <Bell className="w-6 h-6" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
    <div className="max-w-4xl mx-auto p-4 md:p-6 pb-24 md:pb-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Benachrichtigungen
        </h1>
        <p className="text-gray-600">
          {unreadCount > 0
            ? `${unreadCount} ungelesene Benachrichtigung${unreadCount > 1 ? 'en' : ''}`
            : 'Alle Benachrichtigungen gelesen'
          }
        </p>
      </div>

      {/* Filter & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Alle
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'unread'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ungelesen ({unreadCount})
          </button>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition"
            >
              <CheckCheck className="w-5 h-5" />
              <span className="hidden sm:inline">Alle als gelesen</span>
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
            >
              <Trash2 className="w-5 h-5" />
              <span className="hidden sm:inline">Alle löschen</span>
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-gray-500">Laden...</p>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">Keine Benachrichtigungen</p>
          <p className="text-gray-400 text-sm mt-1">
            {filter === 'unread' ? 'Alle Benachrichtigungen wurden gelesen' : 'Sie haben noch keine Benachrichtigungen erhalten'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => {
            // Telefonnummer für Rückruf-Anfragen extrahieren
            const phoneNumber = notification.type === 'RUECKRUF_ANFRAGE'
              ? (notification.data?.telefon || extractPhoneNumber(notification.message))
              : null;

            return (
              <div
                key={notification.id}
                className={`bg-white rounded-xl border p-4 transition hover:shadow-md cursor-pointer ${
                  !notification.read ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getPriorityColor(notification.priority)}`}>
                    {getTypeIcon(notification)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="w-2.5 h-2.5 bg-blue-600 rounded-full"></span>
                        )}
                      </div>
                      {notification.priority === 'URGENT' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          Dringend
                        </span>
                      )}
                    </div>

                    {/* Tägliche Erinnerung mit klickbaren Items */}
                    {notification.type === 'TAEGLICHE_ERINNERUNG' && notification.data?.anfragen ? (
                      <div className="mb-3">
                        <p className="text-gray-600 mb-2">{notification.message}</p>
                        <div className="space-y-2">
                          {notification.data.anfragen.map((anfrage) => (
                            <div
                              key={anfrage.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!notification.read) handleMarkAsRead(notification.id);
                                navigate(anfrage.linkTo);
                              }}
                              className="p-2 rounded-lg bg-gray-50 hover:bg-primary/10 cursor-pointer transition group"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-800 group-hover:text-primary transition truncate">
                                    {anfrage.betreff}
                                  </div>
                                  <div className="text-gray-500 text-sm truncate">
                                    {anfrage.kundeName}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="text-xs text-gray-500">{anfrage.status}</div>
                                  <div className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                                    {anfrage.zeitDiff}
                                    {anfrage.istAlt && <span>⚠️</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {notification.data.total > notification.data.angezeigt && (
                            <p className="text-sm text-gray-500 mt-2">
                              ... und {notification.data.total - notification.data.angezeigt} weitere
                            </p>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!notification.read) handleMarkAsRead(notification.id);
                              navigate('/anfragen?status=offen');
                            }}
                            className="mt-3 w-full py-2 px-4 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition text-center"
                          >
                            Alle anzeigen
                          </button>
                        </div>
                      </div>
                    ) : notification.type === 'TAEGLICHE_ERINNERUNG' && notification.data?.termine ? (
                      <div className="mb-3">
                        <p className="text-gray-600 mb-2">{notification.message}</p>
                        <div className="space-y-2">
                          {notification.data.termine.map((termin) => (
                            <div
                              key={termin.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!notification.read) handleMarkAsRead(notification.id);
                                navigate(termin.linkTo);
                              }}
                              className="p-2 rounded-lg bg-gray-50 hover:bg-primary/10 cursor-pointer transition group"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-primary shrink-0">{termin.uhrzeit}</span>
                                    <span className="font-medium text-gray-800 group-hover:text-primary transition truncate">
                                      {termin.titel}
                                    </span>
                                  </div>
                                  {termin.kundeName && (
                                    <div className="text-gray-500 text-sm truncate">
                                      {termin.kundeName}
                                    </div>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 capitalize shrink-0">
                                  {termin.tag}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600 mb-3 whitespace-pre-wrap">
                        {notification.message}
                      </p>
                    )}

                    {/* Anruf-Button für Rückruf-Anfragen */}
                    {phoneNumber && (
                      <div className="mb-3">
                        <a
                          href={`tel:${phoneNumber}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!notification.read) {
                              handleMarkAsRead(notification.id);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                        >
                          <Phone className="w-4 h-4" />
                          Jetzt anrufen: {phoneNumber}
                        </a>
                      </div>
                    )}

                    {/* Terminvorschlag/Stornierung Bestätigen/Ablehnen Buttons */}
                    {isAppointmentSuggestion(notification) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          onClick={(e) => handleConfirmAppointment(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                            notification.data?.typ === 'STORNIERUNG'
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {isProcessing(notification.id) ? 'Wird bearbeitet...' : getActionLabels(notification).confirm}
                        </button>
                        {notification.data?.typ !== 'STORNIERUNG' && (
                          <button
                            onClick={(e) => handleCounterPropose(notification, e)}
                            disabled={isProcessing(notification.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Calendar className="w-4 h-4" />
                            Anderen Termin vorschlagen
                          </button>
                        )}
                        <button
                          onClick={(e) => handleRejectAppointment(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircle className="w-4 h-4" />
                          {getActionLabels(notification).reject}
                        </button>
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Manuell erledigt - Benachrichtigung verwerfen"
                        >
                          <Trash2 className="w-4 h-4" />
                          Verwerfen
                        </button>
                      </div>
                    )}

                    {/* KVA Bestätigen/Ablehnen/Bearbeiten Buttons */}
                    {isKvaBestaetigung(notification) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          onClick={(e) => handleConfirmKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {isProcessing(notification.id) ? 'Wird gesendet...' : 'KVA senden'}
                        </button>
                        <button
                          onClick={(e) => handleEditKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FileText className="w-4 h-4" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={(e) => handleRejectKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircle className="w-4 h-4" />
                          Ablehnen
                        </button>
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Manuell erledigt - Benachrichtigung verwerfen"
                        >
                          <Trash2 className="w-4 h-4" />
                          Verwerfen
                        </button>
                      </div>
                    )}

                    {/* AB Bestätigen/Bearbeiten/Ablehnen Buttons */}
                    {isAbBestaetigung(notification) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          onClick={(e) => handleConfirmKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {isProcessing(notification.id) ? 'Wird versendet...' : 'AB versenden'}
                        </button>
                        <button
                          onClick={(e) => handleEditKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FileText className="w-4 h-4" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={(e) => handleRejectKva(notification, e)}
                          disabled={isProcessing(notification.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircle className="w-4 h-4" />
                          Ablehnen
                        </button>
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition text-sm font-medium"
                          title="Manuell erledigt - Benachrichtigung verwerfen"
                        >
                          <Trash2 className="w-4 h-4" />
                          Verwerfen
                        </button>
                      </div>
                    )}

                    {/* Standard Actions - nur bei unread und keine Spezial-Buttons */}
                    {notification.actions && notification.actions.length > 0 && !notification.read && !phoneNumber && !isAppointmentSuggestion(notification) && !isKvaBestaetigung(notification) && !isAbBestaetigung(notification) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {notification.actions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => handleAction(notification, action, e)}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition text-sm font-medium"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(notification.createdAt)}
                      </span>

                      <div className="flex items-center gap-1">
                        {/* Link-Hinweis nur wenn linkTo vorhanden und KEINE klickbaren Items */}
                        {notification.linkTo && notification.type !== 'TAEGLICHE_ERINNERUNG' && (
                          <span className="text-xs text-primary mr-2">
                            Klicken zum Anzeigen →
                          </span>
                        )}
                        {!notification.read && (
                          <button
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition"
                            title="Als gelesen markieren"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Löschen"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    <CounterProposalModal
      isOpen={!!counterProposalNotification}
      onClose={() => setCounterProposalNotification(null)}
      onSubmit={handleSubmitCounterProposal}
      isLoading={counterProposalLoading}
      originalTermin={counterProposalNotification?.data?.terminDaten
        ? `${counterProposalNotification.data.terminDaten.datum} um ${counterProposalNotification.data.terminDaten.uhrzeit} Uhr`
        : null}
    />
    </Layout>
  );
}
